'use client'

import React, { useState, useEffect } from 'react'
import { 
  Plus, 
  Search, 
  Filter, 
  Edit2, 
  Trash2, 
  CheckCircle, 
  Clock, 
  XCircle,
  Package,
  DollarSign,
  Percent,
  FileText,
  AlertCircle,
  User,
  Calendar,
  Sparkles,
  Zap,
  Settings
} from 'lucide-react'
import Layout from '../../../components/vision/Layout'
import ProtectedRoute from '../../../components/vision/ProtectedRoute'
import ServiceDropdownSelector from '../../../components/vision/ServiceDropdownSelector'
import ClientSearchableDropdown from '../../../components/vision/ClientSearchableDropdown'
import CondicoesEspeciais from '../../../components/vision/CondicoesEspeciais'
import ServicoAvulsoConfig from '../../../components/vision/ServicoAvulsoConfig'

interface Cliente {
  id: string
  nome_completo: string
  nome_empresa?: string
  email?: string
}

interface Servico {
  id: string
  nome: string
  valor_padrao: number
}

interface ServicoSelecionado {
  servico_id: string
  valor_customizado?: number
}

interface ServicoAvulsoPagamento {
  numero_parcela: number
  data_pagamento: string
  valor: number
}

interface ServicoAvulsoConfig {
  servico_id: string
  numero_parcelas: number
  valor_total: number
  pagamentos: ServicoAvulsoPagamento[]
}

interface PlanoServico {
  id: string
  servico_id: string
  valor_customizado?: number
  servicos: Servico
  servico_avulso_config?: ServicoAvulsoConfig
}

interface CondicaoEspecial {
  id: string
  mes_inicio: number
  mes_fim: number
  tipo_condicao: string
  valor: number
  descricao?: string
}

interface Plano {
  id: string
  cliente_id: string
  nome: string
  data_inicio: string
  data_fim?: string
  dia_vencimento: number
  valor_base: number
  status: string
  observacoes?: string
  created_at: string
  clientes: Cliente
  plano_servicos: PlanoServico[]
  plano_condicoes_especiais: CondicaoEspecial[]
}

export default function PlanosPage() {
  const [planos, setPlanos] = useState<Plano[]>([])
  const [clientes, setClientes] = useState<Cliente[]>([])
  const [servicos, setServicos] = useState<Servico[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [showModal, setShowModal] = useState(false)
  const [editingPlano, setEditingPlano] = useState<Plano | null>(null)
  const [formData, setFormData] = useState({
    tipo_venda: '' as 'plano_recorrente' | 'venda_avulsa' | '',
    cliente_id: '',
    nome: '',
    data_inicio: '',
    data_fim: '',
    dia_vencimento: 5,
    valor_base: 0,
    observacoes: '',
    servicos: [] as { servico_id: string; valor_customizado?: number }[],
    condicoes_especiais: [] as { mes_inicio: number; mes_fim: number; tipo_condicao: string; valor: number; descricao?: string }[],
    desconto_geral_tipo: '' as 'percentual' | 'fixo' | '',
    desconto_geral_valor: 0
  })
  const [descontoDisplay, setDescontoDisplay] = useState('')
  const [isEditingDesconto, setIsEditingDesconto] = useState(false)
  const [servicosAvulsoConfig, setServicosAvulsoConfig] = useState<ServicoAvulsoConfig[]>([])
  const [isViewMode, setIsViewMode] = useState(false)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    setLoading(true)
    try {
      // Buscar planos
      const planosResponse = await fetch('/api/planos')
      const planosData = await planosResponse.json()
      if (planosData.data) {
        setPlanos(planosData.data)
      }

      // Buscar clientes
      const clientesResponse = await fetch('/api/gestao/clientes')
      const clientesData = await clientesResponse.json()
      if (clientesData.data) {
        setClientes(clientesData.data)
      }

      // Buscar serviços (assumindo que existe uma API)
      try {
        const servicosResponse = await fetch('/api/servicos')
        const servicosData = await servicosResponse.json()
        if (servicosData.data) {
          setServicos(servicosData.data)
        }
      } catch (error) {
        console.log('API de serviços não disponível, usando lista vazia')
        setServicos([])
      }

    } catch (error) {
      console.error('Erro ao carregar dados:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    // Validar se o tipo de venda foi selecionado (apenas para novos registros)
    if (!editingPlano && !formData.tipo_venda) {
      alert('Selecione o tipo de venda antes de continuar.')
      return
    }
    
    // Validar serviços avulsos antes de submeter
    if (formData.tipo_venda === 'venda_avulsa' || servicosAvulsoConfig.length > 0) {
      const servicosInvalidos = servicosAvulsoConfig.filter(config => {
        const desconto = {
          tipo: formData.desconto_geral_tipo,
          valor: formData.desconto_geral_valor
        }
        
        // Calcular valor final com desconto para cada serviço
        const servico = servicos.find(s => s.id === config.servico_id)
        if (!servico) return false
        
        let valorFinal = config.valor_total
        if (desconto.tipo === 'percentual' && desconto.valor > 0) {
          valorFinal = config.valor_total * (1 - desconto.valor / 100)
        } else if (desconto.tipo === 'fixo' && desconto.valor > 0) {
          valorFinal = Math.max(0, config.valor_total - desconto.valor)
        }
        
        const somaParcelas = config.pagamentos.reduce((sum, p) => sum + p.valor, 0)
        const diferenca = Math.abs(somaParcelas - valorFinal)
        return diferenca >= 0.01 // Inválido se diferença >= 1 centavo
      })
      
      if (servicosInvalidos.length > 0) {
        alert('Corrija os valores das parcelas dos serviços avulsos antes de salvar. A soma das parcelas deve ser igual ao valor final com desconto.')
        return
      }
    }
    
    try {
      // Calcula o valor base automaticamente antes de enviar
      const valorBaseCalculado = calcularValorBase()
      
      const dadosParaEnviar = {
        ...formData,
        valor_base: valorBaseCalculado,
        // Converte string vazia para null para campos de data opcionais
        data_fim: formData.data_fim === '' ? null : formData.data_fim,
        // Adicionar configurações de serviços avulsos (parcelas)
        servicos_avulso_config: servicosAvulsoConfig
      }

      const url = editingPlano ? `/api/planos?id=${editingPlano.id}` : '/api/planos'
      const method = editingPlano ? 'PUT' : 'POST'

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(dadosParaEnviar)
      })

      const result = await response.json()

      if (response.ok) {
        await fetchData() // Recarregar dados
        setShowModal(false)
        resetForm()
      } else {
        console.error('Erro ao salvar plano:', result.error)
        alert('Erro ao salvar plano: ' + result.error)
      }
    } catch (error) {
      console.error('Erro ao salvar plano:', error)
      alert('Erro ao salvar plano')
    }
  }

  const handleDelete = async (planoId: string) => {
    if (!confirm('Tem certeza que deseja excluir este plano?')) {
      return
    }

    try {
      const response = await fetch(`/api/planos?id=${planoId}`, {
        method: 'DELETE'
      })

      if (response.ok) {
        await fetchData()
      } else {
        const result = await response.json()
        alert('Erro ao excluir plano: ' + result.error)
      }
    } catch (error) {
      console.error('Erro ao excluir plano:', error)
      alert('Erro ao excluir plano')
    }
  }

  const openEditModal = (plano: Plano) => {
    setEditingPlano(plano)
    setIsViewMode(false)
    setFormData({
      tipo_venda: (plano as any).tipo_venda || 'plano_recorrente',
      cliente_id: plano.cliente_id,
      nome: plano.nome,
      data_inicio: plano.data_inicio,
      data_fim: plano.data_fim || '',
      dia_vencimento: plano.dia_vencimento,
      valor_base: plano.valor_base,
      observacoes: plano.observacoes || '',
      servicos: plano.plano_servicos.map(ps => ({
        servico_id: ps.servico_id,
        valor_customizado: ps.valor_customizado
      })),
      condicoes_especiais: plano.plano_condicoes_especiais.map(ce => ({
        mes_inicio: ce.mes_inicio,
        mes_fim: ce.mes_fim,
        tipo_condicao: ce.tipo_condicao as 'desconto_percentual' | 'desconto_fixo' | 'valor_fixo',
        valor: ce.valor,
        descricao: ce.descricao
      })),
      desconto_geral_tipo: (plano as any).desconto_geral_tipo || '',
      desconto_geral_valor: (plano as any).desconto_geral_valor || 0
    })
    
    // Carregar parcelas se existirem
    try {
      if ((plano as any).plano_taxa_start_pagamentos && (plano as any).plano_taxa_start_pagamentos.length > 0) {
        const parcelasPorServico: Record<string, any[]> = {}
        
        // Agrupar parcelas por servico_id
        (plano as any).plano_taxa_start_pagamentos.forEach((parcela: any) => {
          const servicoId = parcela.servico_id || 'sem_servico'
          if (!parcelasPorServico[servicoId]) {
            parcelasPorServico[servicoId] = []
          }
          parcelasPorServico[servicoId].push(parcela)
        })
        
        // Criar configuração de serviços avulsos
        const configs = Object.keys(parcelasPorServico).map(servicoId => {
          const parcelas = parcelasPorServico[servicoId].sort((a: any, b: any) => a.numero_parcela - b.numero_parcela)
          const valorTotal = parcelas.reduce((sum: number, p: any) => sum + parseFloat(p.valor || 0), 0)
          
          return {
            servico_id: servicoId,
            numero_parcelas: parcelas.length,
            valor_total: valorTotal,
            pagamentos: parcelas.map((p: any) => ({
              numero_parcela: p.numero_parcela,
              data_pagamento: p.data_pagamento,
              valor: parseFloat(p.valor || 0)
            }))
          }
        })
        
        setServicosAvulsoConfig(configs)
      } else {
        setServicosAvulsoConfig([])
      }
    } catch (error) {
      console.error('Erro ao carregar parcelas no openEditModal:', error)
      setServicosAvulsoConfig([])
    }
    
    setShowModal(true)
  }

  const openViewModal = (plano: Plano) => {
    setEditingPlano(plano)
    setIsViewMode(true)
    setFormData({
      tipo_venda: (plano as any).tipo_venda || 'plano_recorrente',
      cliente_id: plano.cliente_id,
      nome: plano.nome,
      data_inicio: plano.data_inicio,
      data_fim: plano.data_fim || '',
      dia_vencimento: plano.dia_vencimento,
      valor_base: plano.valor_base,
      observacoes: plano.observacoes || '',
      servicos: plano.plano_servicos.map(ps => ({
        servico_id: ps.servico_id,
        valor_customizado: ps.valor_customizado
      })),
      condicoes_especiais: plano.plano_condicoes_especiais.map(ce => ({
        mes_inicio: ce.mes_inicio,
        mes_fim: ce.mes_fim,
        tipo_condicao: ce.tipo_condicao as 'desconto_percentual' | 'desconto_fixo' | 'valor_fixo',
        valor: ce.valor,
        descricao: ce.descricao
      })),
      desconto_geral_tipo: (plano as any).desconto_geral_tipo || '',
      desconto_geral_valor: (plano as any).desconto_geral_valor || 0
    })
    
    // Carregar parcelas se existirem
    try {
      if ((plano as any).plano_taxa_start_pagamentos && (plano as any).plano_taxa_start_pagamentos.length > 0) {
        const parcelasPorServico: Record<string, any[]> = {}
        
        // Agrupar parcelas por servico_id
        (plano as any).plano_taxa_start_pagamentos.forEach((parcela: any) => {
          const servicoId = parcela.servico_id || 'sem_servico'
          if (!parcelasPorServico[servicoId]) {
            parcelasPorServico[servicoId] = []
          }
          parcelasPorServico[servicoId].push(parcela)
        })
        
        // Criar configuração de serviços avulsos
        const configs = Object.keys(parcelasPorServico).map(servicoId => {
          const parcelas = parcelasPorServico[servicoId].sort((a: any, b: any) => a.numero_parcela - b.numero_parcela)
          const valorTotal = parcelas.reduce((sum: number, p: any) => sum + parseFloat(p.valor || 0), 0)
          
          return {
            servico_id: servicoId,
            numero_parcelas: parcelas.length,
            valor_total: valorTotal,
            pagamentos: parcelas.map((p: any) => ({
              numero_parcela: p.numero_parcela,
              data_pagamento: p.data_pagamento,
              valor: parseFloat(p.valor || 0)
            }))
          }
        })
        
        setServicosAvulsoConfig(configs)
      } else {
        setServicosAvulsoConfig([])
      }
    } catch (error) {
      console.error('Erro ao carregar parcelas no openViewModal:', error)
      setServicosAvulsoConfig([])
    }
    
    setShowModal(true)
  }

  const resetForm = () => {
    setEditingPlano(null)
    setFormData({
      tipo_venda: '' as 'plano_recorrente' | 'venda_avulsa' | '',
      cliente_id: '',
      nome: '',
      data_inicio: '',
      data_fim: '',
      dia_vencimento: 5,
      valor_base: 0,
      observacoes: '',
      servicos: [],
      condicoes_especiais: [],
      desconto_geral_tipo: '',
      desconto_geral_valor: 0
    })
    setServicosAvulsoConfig([])
  }

  const filteredPlanos = planos.filter(plano => {
    const matchesSearch = (plano.nome?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
                         (plano.clientes?.nome_completo?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
                         (plano.clientes?.nome_empresa?.toLowerCase() || '').includes(searchTerm.toLowerCase())
    
    const matchesStatus = statusFilter === 'all' || plano.status === statusFilter
    
    return matchesSearch && matchesStatus
  })

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0)
  }

  const calcularTotalServicos = () => {
    return formData.servicos.reduce((total, servicoSel) => {
      const servico = servicos.find(s => s.id === servicoSel.servico_id)
      if (!servico) return total
      
      return total + servico.valor_padrao
    }, 0)
  }

  const calcularValorBase = () => {
    const totalServicos = calcularTotalServicos()
    
    if (!formData.desconto_geral_tipo || formData.desconto_geral_valor === 0) {
      return totalServicos
    }
    
    if (formData.desconto_geral_tipo === 'percentual') {
      return totalServicos * (1 - formData.desconto_geral_valor / 100)
    } else if (formData.desconto_geral_tipo === 'fixo') {
      return Math.max(0, totalServicos - formData.desconto_geral_valor)
    }
    
    return totalServicos
  }

  // Filtrar serviços baseado no tipo de venda
  const servicosDisponiveis = useMemo(() => {
    if (formData.tipo_venda === 'venda_avulsa') {
      return servicos.filter(s => s.servico_avulso === true)
    }
    return servicos // Todos os serviços para planos recorrentes
  }, [formData.tipo_venda, servicos])

  // Filtrar serviços com servico_avulso
  const servicosComServicoAvulso = servicosDisponiveis.filter(servico => 
    formData.servicos.some(s => s.servico_id === servico.id) && 
    servico.servico_avulso
  )

  // Handlers para Serviço Avulso
  const handleServicoAvulsoChange = (servicoId: string, config: ServicoAvulsoConfig) => {
    setServicosAvulsoConfig(prev => {
      const existing = prev.findIndex(c => c.servico_id === servicoId)
      if (existing >= 0) {
        const updated = [...prev]
        updated[existing] = config
        return updated
      } else {
        return [...prev, config]
      }
    })
  }

  const handleServicoAvulsoRemove = (servicoId: string) => {
    setServicosAvulsoConfig(prev => prev.filter(c => c.servico_id !== servicoId))
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR')
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'ativo':
        return <CheckCircle className="w-4 h-4 text-green-600" />
      case 'pausado':
        return <Clock className="w-4 h-4 text-yellow-600" />
      case 'cancelado':
        return <XCircle className="w-4 h-4 text-red-600" />
      case 'finalizado':
        return <AlertCircle className="w-4 h-4 text-gray-600" />
      default:
        return <AlertCircle className="w-4 h-4 text-gray-600" />
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ativo':
        return 'bg-green-100 text-green-800'
      case 'pausado':
        return 'bg-yellow-100 text-yellow-800'
      case 'cancelado':
        return 'bg-red-100 text-red-800'
      case 'finalizado':
        return 'bg-gray-100 text-gray-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  if (loading) {
    return (
      <ProtectedRoute>
        <Layout>
          <div className="min-h-screen bg-gray-50 flex items-center justify-center">
            <div className="flex items-center space-x-3">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
              <span className="text-gray-600">Carregando planos...</span>
            </div>
          </div>
        </Layout>
      </ProtectedRoute>
    )
  }

  return (
    <ProtectedRoute>
      <Layout>
        <div className="min-h-screen bg-gray-50">
          {/* Header */}
          <div className="bg-white shadow-sm border-b border-gray-200">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex items-center justify-between h-16">
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Gestão de Planos</h1>
                  <p className="text-sm text-gray-600">Gerencie planos de serviços dos clientes</p>
                </div>
                <button
                  onClick={() => {
                    resetForm()
                    setShowModal(true)
                  }}
                  className="flex items-center space-x-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  <span>Novo Plano</span>
                </button>
              </div>
            </div>
          </div>

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {/* Filtros */}
            <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="text"
                      placeholder="Buscar por nome do plano, cliente ou empresa..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>
                </div>
                <div className="sm:w-48">
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  >
                    <option value="all">Todos os status</option>
                    <option value="ativo">Ativo</option>
                    <option value="pausado">Pausado</option>
                    <option value="cancelado">Cancelado</option>
                    <option value="finalizado">Finalizado</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Lista de Planos */}
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              {filteredPlanos.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhum plano encontrado</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    {planos.length === 0 
                      ? 'Comece criando um novo plano para seus clientes.'
                      : 'Tente ajustar os filtros de busca.'
                    }
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Plano / Cliente
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Período
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Valor
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Ações
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredPlanos.map((plano) => (
                        <tr key={plano.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {plano.nome}
                              </div>
                              <div className="text-sm text-gray-500">
                                <User className="inline w-3 h-3 mr-1" />
                                {plano.clientes?.nome_completo || 'Cliente não encontrado'}
                              </div>
                              {plano.clientes?.nome_empresa && (
                                <div className="text-sm text-gray-500">
                                  {plano.clientes.nome_empresa}
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              <Calendar className="inline w-3 h-3 mr-1" />
                              {formatDate(plano.data_inicio)}
                            </div>
                            {plano.data_fim && (
                              <div className="text-sm text-gray-500">
                                até {formatDate(plano.data_fim)}
                              </div>
                            )}
                            <div className="text-sm text-gray-500">
                              Venc: dia {plano.dia_vencimento}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              <DollarSign className="inline w-3 h-3 mr-1" />
                              {formatCurrency(plano.valor_base)}
                            </div>
                            <div className="text-sm text-gray-500">
                              {plano.plano_servicos?.length || 0} serviço(s)
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(plano.status)}`}>
                              {getStatusIcon(plano.status)}
                              <span className="ml-1 capitalize">{plano.status}</span>
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex space-x-2">
                              <button
                                onClick={() => openViewModal(plano)}
                                className="text-blue-600 hover:text-blue-900 transition-colors"
                                title="Visualizar plano"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                              </button>
                              <button
                                onClick={() => openEditModal(plano)}
                                className="text-orange-600 hover:text-orange-900 transition-colors"
                                title="Editar plano"
                              >
                                <Edit2 className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDelete(plano.id)}
                                className="text-red-600 hover:text-red-900 transition-colors"
                                title="Excluir plano"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {/* Modal de Criação/Edição */}
          {showModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-xl max-w-4xl w-full max-h-[95vh] overflow-y-auto shadow-2xl">
                {/* Header Sofisticado */}
                <div className="bg-gradient-to-r from-emerald-50 to-emerald-100 border-b border-emerald-200 px-8 py-6 rounded-t-xl">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-emerald-500 rounded-lg shadow-sm">
                      <Sparkles className="w-5 h-5 text-white" />
                    </div>
                    <h2 className="text-2xl font-bold text-emerald-900">
                      {editingPlano ? 'Editar Plano' : 'Nova Venda'}
                    </h2>
                  </div>
                </div>
                
                <div className="p-8">

                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Seletor de Tipo de Venda */}
                    {!editingPlano && (
                      <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-6 rounded-xl border border-gray-200">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">
                          Tipo de Venda <span className="text-red-500">*</span>
                        </h3>
                        <div className="flex space-x-6">
                          <label className="flex items-center cursor-pointer">
                            <input
                              type="radio"
                              name="tipo_venda"
                              value="plano_recorrente"
                              checked={formData.tipo_venda === 'plano_recorrente'}
                              onChange={(e) => setFormData({ ...formData, tipo_venda: e.target.value as 'plano_recorrente' | 'venda_avulsa' })}
                              className="w-4 h-4 text-emerald-600 bg-gray-100 border-gray-300 focus:ring-emerald-500 focus:ring-2"
                            />
                            <span className="ml-2 text-sm font-medium text-gray-900">Plano Recorrente</span>
                          </label>
                          <label className="flex items-center cursor-pointer">
                            <input
                              type="radio"
                              name="tipo_venda"
                              value="venda_avulsa"
                              checked={formData.tipo_venda === 'venda_avulsa'}
                              onChange={(e) => setFormData({ ...formData, tipo_venda: e.target.value as 'plano_recorrente' | 'venda_avulsa' })}
                              className="w-4 h-4 text-emerald-600 bg-gray-100 border-gray-300 focus:ring-emerald-500 focus:ring-2"
                            />
                            <span className="ml-2 text-sm font-medium text-gray-900">Venda Avulsa</span>
                          </label>
                        </div>
                      </div>
                    )}

                    {/* Grid responsivo para melhor distribuição */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {/* Coluna esquerda */}
                      <div className="space-y-4">
                        {/* Cliente */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Cliente *
                          </label>
                          <ClientSearchableDropdown
                            clientes={clientes}
                            selectedClientId={formData.cliente_id}
                            onSelect={(clienteId) => setFormData({ ...formData, cliente_id: clienteId })}
                            placeholder="Selecione um cliente"
                            required={true}
                          />
                        </div>

                        {/* Data de Início - apenas para planos recorrentes */}
                        {formData.tipo_venda === 'plano_recorrente' && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Data de Início *
                            </label>
                            <input
                              type="date"
                              value={formData.data_inicio}
                              onChange={(e) => setFormData({ ...formData, data_inicio: e.target.value })}
                              required
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                            />
                          </div>
                        )}

                        {/* Dia de Vencimento - apenas para planos recorrentes */}
                        {formData.tipo_venda === 'plano_recorrente' && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Dia de Vencimento
                            </label>
                            <select
                              value={formData.dia_vencimento}
                              onChange={(e) => setFormData({ ...formData, dia_vencimento: parseInt(e.target.value) })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                            >
                              {Array.from({ length: 28 }, (_, i) => i + 1).map(day => (
                                <option key={day} value={day}>Dia {day}</option>
                              ))}
                            </select>
                          </div>
                        )}
                      </div>

                      {/* Coluna direita */}
                      <div className="space-y-4">
                        {/* Nome do Plano/Venda */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {formData.tipo_venda === 'plano_recorrente' ? 'Nome do Plano *' : 'Descrição da Venda *'}
                          </label>
                          <input
                            type="text"
                            value={formData.nome}
                            onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                            required
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                            placeholder="Ex: Plano Marketing Digital Premium"
                          />
                        </div>

                        {/* Data de Fim - apenas para planos recorrentes */}
                        {formData.tipo_venda === 'plano_recorrente' && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Data de Fim (opcional)
                            </label>
                            <input
                              type="date"
                              value={formData.data_fim}
                              onChange={(e) => setFormData({ ...formData, data_fim: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                            />
                          </div>
                        )}

                        {/* Valor Base (Calculado) */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Valor Base Mensal (Calculado Automaticamente)
                          </label>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">
                              R$
                            </span>
                            <input
                              type="text"
                              value={formatCurrency(calcularValorBase())}
                              readOnly
                              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-700 cursor-not-allowed"
                              title="Valor calculado automaticamente com base nos serviços selecionados e desconto aplicado"
                            />
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            Calculado: Serviços ({formatCurrency(calcularTotalServicos())}) 
                            {formData.desconto_geral_tipo && formData.desconto_geral_valor > 0 && (
                              <span>
                                {' - '}
                                {formData.desconto_geral_tipo === 'percentual' 
                                  ? `${formData.desconto_geral_valor}%` 
                                  : formatCurrency(formData.desconto_geral_valor)
                                } desconto
                              </span>
                            )}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Seções full-width para componentes complexos */}
                    <div className="col-span-full space-y-8">
                      {/* Seleção de Serviços - Destaque Laranja */}
                      <div className="bg-gradient-to-br from-orange-50 via-orange-25 to-white p-8 rounded-2xl border border-orange-200 shadow-lg hover:shadow-xl transition-all duration-300">
                        <div className="flex items-center space-x-3 mb-6">
                          <div className="p-3 bg-orange-500 rounded-xl shadow-md">
                            <Package className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <h3 className="text-xl font-bold text-orange-900">Serviços</h3>
                            <p className="text-orange-700 text-sm">Selecione os serviços que farão parte desta venda</p>
                          </div>
                        </div>
                        <ServiceDropdownSelector
                          servicosSelecionados={formData.servicos}
                          onChange={(servicos) => setFormData({ ...formData, servicos })}
                          servicosDisponiveis={servicosDisponiveis}
                        />
                      </div>
                    </div>


                    {/* Desconto Geral */}
                    <div>
                      <h4 className="text-md font-medium text-gray-900 mb-3">Desconto Geral no Plano</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Tipo de Desconto
                          </label>
                          <select
                            value={formData.desconto_geral_tipo}
                            onChange={(e) => setFormData({ 
                              ...formData, 
                              desconto_geral_tipo: e.target.value as 'percentual' | 'fixo' | '',
                              desconto_geral_valor: e.target.value ? formData.desconto_geral_valor : 0
                            })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                          >
                            <option value="">Sem desconto geral</option>
                            <option value="percentual">Desconto Percentual (%)</option>
                            <option value="fixo">Desconto Fixo (R$)</option>
                          </select>
                        </div>
                        
                        {formData.desconto_geral_tipo && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              {formData.desconto_geral_tipo === 'percentual' ? 'Percentual (%)' : 'Valor (R$)'}
                            </label>
                            <div className="relative">
                              {formData.desconto_geral_tipo === 'percentual' ? (
                                <Percent className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                              ) : (
                                <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                              )}
                              <input
                                type="text"
                                value={descontoDisplay}
                                onChange={(e) => {
                                  if (formData.desconto_geral_tipo === 'percentual') {
                                    // Para percentual, só permite números e limita a 100
                                    const numbersOnly = e.target.value.replace(/\D/g, '');
                                    const numericValue = Math.min(parseInt(numbersOnly) || 0, 100);
                                    
                                    setDescontoDisplay(numericValue.toString());
                                    setFormData({ ...formData, desconto_geral_valor: numericValue });
                                  } else {
                                    // Para valor fixo, aplica formatação de moeda
                                    const numbersOnly = e.target.value.replace(/\D/g, '');
                                    
                                    if (numbersOnly === '') {
                                      setDescontoDisplay('');
                                      setFormData({ ...formData, desconto_geral_valor: 0 });
                                      return;
                                    }
                                    
                                    // Converte para número (centavos)
                                    const numericValue = parseInt(numbersOnly) / 100;
                                    
                                    // Formata em tempo real como moeda brasileira
                                    const formatted = numericValue.toLocaleString('pt-BR', {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2
                                    });
                                    
                                    setDescontoDisplay(formatted);
                                    setFormData({ ...formData, desconto_geral_valor: numericValue });
                                  }
                                }}
                                onFocus={() => {
                                  setIsEditingDesconto(true);
                                  if (formData.desconto_geral_tipo === 'percentual') {
                                    setDescontoDisplay(formData.desconto_geral_valor > 0 ? formData.desconto_geral_valor.toString() : '');
                                  } else {
                                    if (formData.desconto_geral_valor === 0) {
                                      setDescontoDisplay('');
                                    } else {
                                      setDescontoDisplay(formData.desconto_geral_valor.toLocaleString('pt-BR', {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2
                                      }));
                                    }
                                  }
                                }}
                                onBlur={() => {
                                  setIsEditingDesconto(false);
                                  // Garante formatação final
                                  if (formData.desconto_geral_tipo === 'percentual') {
                                    setDescontoDisplay(formData.desconto_geral_valor > 0 ? formData.desconto_geral_valor.toString() : '');
                                  } else {
                                    if (formData.desconto_geral_valor > 0) {
                                      setDescontoDisplay(formData.desconto_geral_valor.toLocaleString('pt-BR', {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2
                                      }));
                                    }
                                  }
                                }}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                                placeholder={formData.desconto_geral_tipo === 'percentual' ? '0' : '0,00'}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Serviço Avulso - só aparece se houver serviços com servico_avulso */}
                    {servicosComServicoAvulso.length > 0 && (
                      <div className="bg-gradient-to-br from-yellow-50 via-amber-25 to-white p-8 rounded-2xl border border-yellow-200 shadow-lg hover:shadow-xl transition-all duration-300">
                        <div className="flex items-center space-x-3 mb-6">
                          <div className="p-3 bg-yellow-500 rounded-xl shadow-md">
                            <Zap className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <h3 className="text-xl font-bold text-yellow-900">Configuração de Serviço Avulso</h3>
                            <p className="text-yellow-700 text-sm">Configure o parcelamento e datas do serviço avulso</p>
                          </div>
                        </div>
                        <div className="space-y-6">
                          {servicosComServicoAvulso.map(servico => (
                            <ServicoAvulsoConfig 
                              key={servico.id}
                              servico={servico}
                              config={servicosAvulsoConfig.find(c => c.servico_id === servico.id)}
                              desconto={{
                                tipo: formData.desconto_geral_tipo,
                                valor: formData.desconto_geral_valor
                              }}
                              onChange={handleServicoAvulsoChange}
                              onRemove={handleServicoAvulsoRemove}
                            />
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Condições Especiais - apenas para planos recorrentes */}
                    {formData.tipo_venda === 'plano_recorrente' && (
                      <div className="bg-gradient-to-br from-purple-50 via-violet-25 to-white p-8 rounded-2xl border border-purple-200 shadow-lg hover:shadow-xl transition-all duration-300">
                        <div className="flex items-center space-x-3 mb-6">
                          <div className="p-3 bg-purple-500 rounded-xl shadow-md">
                            <Percent className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <h3 className="text-xl font-bold text-purple-900">Condições Especiais</h3>
                            <p className="text-purple-700 text-sm">Configure descontos por período específico</p>
                          </div>
                        </div>
                        <CondicoesEspeciais
                          condicoes={formData.condicoes_especiais}
                          onChange={(condicoes) => setFormData({ ...formData, condicoes_especiais: condicoes })}
                          valorBase={calcularValorBase()}
                        />
                      </div>
                    )}

                    {/* Observações */}
                    <div className="bg-gradient-to-br from-slate-50 to-white p-8 rounded-2xl border border-slate-200 shadow-lg hover:shadow-xl transition-all duration-300">
                      <div className="flex items-center space-x-3 mb-6">
                        <div className="p-3 bg-slate-500 rounded-xl shadow-md">
                          <FileText className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <h3 className="text-xl font-bold text-slate-900">Observações</h3>
                          <p className="text-slate-700 text-sm">Informações adicionais e detalhes importantes</p>
                        </div>
                      </div>
                      <textarea
                        value={formData.observacoes}
                        onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
                        rows={4}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-slate-500 focus:border-slate-500 transition-all duration-200 bg-white shadow-sm resize-none"
                        placeholder="Informações adicionais sobre o plano, condições especiais, observações importantes..."
                      />
                    </div>

                    {/* Botões Sofisticados */}
                    <div className="flex justify-end space-x-4 pt-8 border-t border-gray-200">
                      <button
                        type="button"
                        onClick={() => setShowModal(false)}
                        className="px-6 py-3 text-gray-700 bg-gradient-to-r from-gray-100 to-gray-200 rounded-xl hover:from-gray-200 hover:to-gray-300 transition-all duration-200 font-medium shadow-sm hover:shadow-md"
                      >
                        Cancelar
                      </button>
                      <button
                        type="submit"
                        className="px-8 py-3 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white rounded-xl hover:from-emerald-600 hover:to-emerald-700 transition-all duration-200 font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 flex items-center space-x-2"
                      >
                        <Sparkles className="w-4 h-4" />
                        <span>{editingPlano ? 'Atualizar' : 'Criar'} Plano</span>
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          )}
        </div>
      </Layout>
    </ProtectedRoute>
  )
}
